import {
  ActionIcon,
  Button,
  Center,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconId, IconPlus, IconTicket, IconX } from "@tabler/icons-react";
import { FEATURE_FLAGS } from "@congrs/schemas";
import { useGetBadgeTemplates } from "../../api/badges";
import { useIsFeatureEnabled } from "../../api/featureFlags";
import { BadgeThumbnail } from "../Badges/BadgeCard";
import { openAttachmentPickerModal } from "./AttachmentPickerModal";

// Fixed box so rows stay aligned whatever each badge's aspect ratio is.
const THUMB_HEIGHT = 48;
const THUMB_BOX_WIDTH = 68;

const CampaignAttachments = ({ attachments, onChange, eventId }) => {
  const badgesEnabled = useIsFeatureEnabled(eventId, FEATURE_FLAGS.BADGES);
  const { data: badges } = useGetBadgeTemplates({ eventId });

  const visibleAttachments = badgesEnabled
    ? attachments
    : attachments.filter((a) => a.type !== "badge");

  const describe = (attachment) => {
    if (attachment.type === "eventTicket") {
      return {
        icon: IconTicket,
        color: "red",
        title: "Ticket PDF",
        subtitle: "Classic PDF ticket with QR code",
      };
    }
    const badge = (badges ?? []).find((b) => b.id === attachment.badgeId);
    return {
      icon: IconId,
      color: "grape",
      badge,
      title: badge?.name ?? "Badge",
      subtitle: "Personalized badge for each recipient",
    };
  };

  const keyFor = (attachment) =>
    attachment.type === "badge"
      ? `badge-${attachment.badgeId}`
      : attachment.type;

  const remove = (attachment) => {
    onChange(attachments.filter((a) => keyFor(a) !== keyFor(attachment)));
  };

  const openPicker = () =>
    openAttachmentPickerModal({
      initialAttachments: attachments,
      onConfirm: onChange,
      eventId,
    });

  return (
    <Paper withBorder shadow="md" p="md" radius="md">
      <Text fw={500}>Attachments</Text>
      <Text size="sm" c="dimmed" mb="md">
        Select what to attach to every email in this campaign.
      </Text>
      <Stack gap="sm">
        {visibleAttachments.map((attachment) => {
          const {
            icon: Icon,
            color,
            title,
            subtitle,
            badge,
          } = describe(attachment);
          return (
            <Paper key={keyFor(attachment)} withBorder p="md" radius="md">
              <Group justify="space-between" wrap="nowrap">
                <Group gap="md" wrap="nowrap">
                  {badge ? (
                    <Center w={THUMB_BOX_WIDTH} style={{ flexShrink: 0 }}>
                      <BadgeThumbnail
                        templateData={badge.templateData}
                        backgroundUrl={badge.backgroundKey}
                        height={THUMB_HEIGHT}
                      />
                    </Center>
                  ) : (
                    <ThemeIcon
                      variant="light"
                      color={color}
                      size="xl"
                      radius="md"
                    >
                      <Icon size={22} />
                    </ThemeIcon>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <Text fw={600} lineClamp={1}>
                      {title}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {subtitle}
                    </Text>
                  </div>
                </Group>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => remove(attachment)}
                  aria-label={`Remove ${title}`}
                >
                  <IconX size={18} />
                </ActionIcon>
              </Group>
            </Paper>
          );
        })}

        <Button
          variant="default"
          leftSection={<IconPlus size={18} />}
          onClick={openPicker}
          styles={{ root: { borderStyle: "dashed" } }}
          fullWidth
        >
          Add attachment
        </Button>
      </Stack>
    </Paper>
  );
};

export default CampaignAttachments;
